import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Chip, Divider, Grid, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import * as Yup from 'yup';
import { FieldArray, Form, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import moment from 'moment/moment';

import useNotification from '../../../../../../core/hooks/use_notification';
import { Button, SubmitButton } from '../../../../../common/button';
import RadioGroupFormik from '../../../../../common/radio_group';
import { yesNoOptions } from '../../../../../common/form_utils';
import { SelectFieldFormik } from '../../../../../common/select_field';
import { getReturnReasonOptions } from '../../../../../common/utils/common';
import TextFieldFormik from '../../../../../common/text_field';
import { MISC_DATE_ISO_FORMAT, MISC_DATE_POLISH_FORMAT } from '../../../../../../core/constants';
import { getOrderStatus, getOrderStatusColor } from '../../../../../common/utils/order_utils';
import { returnOrderNotificationData } from '../../../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../../../core/enums/common/api';
import { returnCustomerOrderAction, returnCustomerOrderState } from '../../../../../../features/order/return_customer_order';

const ReturnOrderDialog = ({ order, refreshList }) => {
    const dispatch = useDispatch();
    const { showNotification } = useNotification();
    const { enqueueSnackbar } = useSnackbar();

    const returnCustomerOrder = useSelector(returnCustomerOrderState);

    const [open, setOpen] = useState(false);

    const initialValues = {
        allPositions: true,
        reason: '',
        productPositions: order?.products
            ? order?.products?.map(product => ({
                orderProductId: product?.id,
                productName: product?.productName,
                sellingPrice: product?.sellingPrice.toFixed(2),
                quantity: product?.quantity,
                newQuantity: product?.quantity,
                isReturned: product?.isReturned,
            }))
            : [],
    };

    const schema = Yup.object().shape({
        allPositions: Yup.boolean().required(),
        reason: Yup.string().required(),
        productPositions: Yup.array().notRequired(),
    });

    const onClose = () => setOpen(false);

    const onSubmit = (values) => {
        let form = {
            reason: values?.reason,
            allPositions: values?.allPositions,
            customerId: order?.customerId,
        };
        let data = {
            orderId: order?.id,
            form
        };

        if (values?.allPositions) {
            dispatch(returnCustomerOrderAction(data))
                .then(response => {
                    showNotification(returnOrderNotificationData(response));
                    if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                        refreshList();
                        onClose();
                    }
                });
        } else {
            const productPositions = values?.productPositions
                ?.map(position => ({ ...position, quantityToReturn: position?.quantity - position?.newQuantity }))
                ?.map(position => ({
                    quantityToReturn: position?.quantityToReturn,
                    orderProductId: position?.orderProductId
                }))
                ?.filter(position => position?.quantityToReturn !== 0);
            form = { ...form, productPositions };
            data = { ...data, form };

            if (productPositions.length === 0) {
                enqueueSnackbar('Nie naniesiono żadnych zmian', { variant: 'error' });
                return;
            }

            dispatch(returnCustomerOrderAction(data))
                .then(response => {
                    showNotification(returnOrderNotificationData(response));
                    if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                        refreshList();
                        onClose();
                    }
                });
        }
    };

    return (
        <>
            <Tooltip title="Utwórz zwrot">
                <IconButton size="small" onClick={ () => setOpen(true) }>
                    <MoneyOffIcon fontSize="small"/>
                </IconButton>
            </Tooltip>

            <Dialog open={ open } onClose={ onClose } fullWidth maxWidth="lg">
                <Formik
                    initialValues={ initialValues }
                    validationSchema={ schema }
                    validateOnChange={ false }
                    validateOnBlur={ false }
                    onSubmit={ onSubmit }>
                    { (formik) =>
                        <Box component={ Form }>
                            <DialogTitle>
                                <Grid container alignItems="center" justifyContent="space-between">
                                    <Grid>
                                        <Typography variant="h6" fontWeight="bold">
                                            Zwrot do rachunku { order?.id }
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Data
                                            zakupu: <b>{ moment(order?.createdDate, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }</b>
                                        </Typography>
                                    </Grid>
                                    <Chip
                                        size="medium"
                                        label={ getOrderStatus(order?.statusName) }
                                        color={ getOrderStatusColor(order?.statusName) }
                                        sx={ { fontWeight: 600 } }
                                    />
                                </Grid>
                            </DialogTitle>
                            <Divider/>

                            <DialogContent sx={ { paddingTop: 2 } }>
                                <Grid container spacing={ 3 }>
                                    <Grid item xs={ 12 }>
                                        <Grid container spacing={ 4 }>
                                            <Grid item xs={ 12 } sm={ 4 }>
                                                <RadioGroupFormik
                                                    name="allPositions"
                                                    label="Wszystkie pozycje"
                                                    options={ yesNoOptions }
                                                />
                                            </Grid>
                                            <Grid item xs={ 12 } sm={ 8 }>
                                                <SelectFieldFormik
                                                    size="small"
                                                    name="reason"
                                                    label="Powód zwrotu"
                                                    options={ getReturnReasonOptions }
                                                    required
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    { !formik?.values?.allPositions && (
                                        <Grid item xs={ 12 }>
                                            <Typography variant="subtitle2" color="textSecondary"
                                                        sx={ { textTransform: 'uppercase', fontWeight: 600, mb: 2 } }>
                                                Wybierz produkty do zwrotu
                                            </Typography>
                                            <FieldArray
                                                name={ 'productPositions' }
                                                render={ () => (
                                                    <TableContainer component={ Paper } variant="outlined">
                                                        <Table stickyHeader size="small">
                                                            <TableHead>
                                                                <TableRow sx={ {
                                                                    '& th': {
                                                                        backgroundColor: 'slate.50',
                                                                        fontWeight: 600
                                                                    }
                                                                } }>
                                                                    <TableCell>Nazwa produktu</TableCell>
                                                                    <TableCell align="center">Cena sprzedaży</TableCell>
                                                                    <TableCell align="center">Oryginalna liczba
                                                                        sztuk</TableCell>
                                                                    <TableCell align="center">Skorygowana liczba
                                                                        sztuk</TableCell>
                                                                    <TableCell align="center">Status</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                { formik?.values?.productPositions?.map((position, index) => (
                                                                    <TableRow key={ index } hover>
                                                                        <TableCell sx={ {
                                                                            textDecoration: position?.isReturned ? 'line-through' : 'none',
                                                                            fontWeight: 500
                                                                        } }>
                                                                            { position?.productName }
                                                                        </TableCell>
                                                                        <TableCell align="center">
                                                                            { position?.sellingPrice } { order?.currency }
                                                                        </TableCell>
                                                                        <TableCell align="center">
                                                                            { position?.quantity }
                                                                        </TableCell>
                                                                        <TableCell align="center" sx={ { width: 150 } }>
                                                                            { !position?.isReturned ? (
                                                                                <TextFieldFormik
                                                                                    name={ `productPositions.${ index }.newQuantity` }
                                                                                    size="small"
                                                                                    type="number"
                                                                                    inputProps={ {
                                                                                        min: '0',
                                                                                        step: '1',
                                                                                        max: position?.quantity
                                                                                    } }
                                                                                    sx={ { background: 'white' } }
                                                                                />
                                                                            ) : '-' }
                                                                        </TableCell>
                                                                        <TableCell align="center">
                                                                            <Chip
                                                                                label={ position?.isReturned ? "Zwrócony" : "Dostępny do zwrotu" }
                                                                                color={ position?.isReturned ? "warning" : "success" }
                                                                                size="small"
                                                                            />
                                                                        </TableCell>
                                                                    </TableRow>
                                                                )) }
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                ) }
                                            />
                                        </Grid>
                                    ) }
                                </Grid>
                            </DialogContent>
                            <DialogActions sx={ { p: 2, borderTop: theme => `1px solid ${ theme.palette.divider }` } }>
                                <Button onClick={ onClose } variant="outlined">Zamknij</Button>
                                <SubmitButton isLoading={ returnCustomerOrder?.loading }>Zapisz zwrot</SubmitButton>
                            </DialogActions>
                        </Box>
                    }
                </Formik>
            </Dialog>
        </>
    );
};

export default ReturnOrderDialog;